/**
 * Vertex/midpoint styling for turf drawing (Mapbox GL Draw).
 *
 * The visible handles are rendered as DOM overlays by TurfVertexOverlay so
 * they can carry hover/focus/drag states, CSS transitions, per-subsidiary
 * accent colours, and hit areas larger than their visual size. The layers
 * defined here are the machinery that makes that overlay work:
 *
 *   • Polygon fill + stroke — the visible blue turf (still painted by Mapbox).
 *   • Vertex + midpoint circles — transparent but hit-testable. Mapbox Draw's
 *     internal drag input uses `queryRenderedFeatures` against these layers to
 *     detect which vertex the user is grabbing; the circle radii are set to
 *     the desired hit-target size (22 = 44px vertex target, 18 = 36px midpoint
 *     target), so an older user with a shaky pointer still lands on the
 *     handle even though the visible disk is much smaller.
 */

/** A Mapbox GL Draw style layer (loosely typed; the draw plugin validates shape). */
export type DrawStyle = {
  id: string;
  type: string;
  filter: unknown[];
  paint?: Record<string, unknown>;
  layout?: Record<string, unknown>;
};

/** Hit-target radius (px) for the invisible vertex circle. Doubled = 44px target. */
export const VERTEX_HIT_RADIUS = 22;
/** Hit-target radius (px) for the invisible midpoint circle. Doubled = 36px target. */
export const MIDPOINT_HIT_RADIUS = 18;

/**
 * The full Mapbox GL Draw `styles` array: blue polygon fill + outline (as
 * before), plus transparent-but-hit-testable circles for vertices and
 * midpoints. The visible handle disks are drawn by TurfVertexOverlay in the
 * DOM; these circles exist solely so Draw's native drag input still works.
 */
export function buildTurfDrawStyles(): DrawStyle[] {
  return [
    {
      id: 'gl-draw-polygon-fill-active',
      type: 'fill',
      filter: ['all', ['==', '$type', 'Polygon']],
      paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.2 },
    },
    {
      id: 'gl-draw-polygon-stroke-active',
      type: 'line',
      filter: ['all', ['==', '$type', 'Polygon']],
      paint: { 'line-color': '#3b82f6', 'line-width': 2 },
    },
    {
      id: 'gl-draw-line-active',
      type: 'line',
      filter: ['all', ['==', '$type', 'LineString']],
      paint: { 'line-color': '#3b82f6', 'line-width': 2, 'line-dasharray': [2, 2] },
    },
    {
      id: 'gl-draw-polygon-and-line-vertex-active',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'vertex']],
      paint: {
        'circle-radius': VERTEX_HIT_RADIUS,
        'circle-opacity': 0,
        'circle-stroke-opacity': 0,
      },
    },
    {
      id: 'gl-draw-polygon-midpoint',
      type: 'circle',
      filter: ['all', ['==', 'meta', 'midpoint']],
      paint: {
        'circle-radius': MIDPOINT_HIT_RADIUS,
        'circle-opacity': 0,
        'circle-stroke-opacity': 0,
      },
    },
  ];
}
